import { useMemo, useState } from 'react';
import { Box, Divider, Grid, Stack, Theme, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { Field, Form, Formik } from 'formik';
import { useAsyncCallback } from 'react-async-hook';
import dayjs, { Dayjs } from 'dayjs';

import { GeneralStatisticsResultDto, ProfitLossStatisticsResultDto } from 'api/investor-client';
import { investorApiClient } from 'api';
import Card from 'components/card';
import { Select } from 'components/forms';
import { InlineTooltip } from 'components/UI';
import { DataOverviewTable, ITableRow, SubmitFormOnChange } from 'components';
import { useFormatMoney } from 'hooks';
import { moveDecimalPoint } from 'utils';
import { makeStyles } from '@mui/styles';

interface TotalAmountProps {
  amount: string | number;
  format?: boolean;
}

interface AmountProps {
  amount: number;
}

// const toPercents = (val: number) => {
//   return `${(val * 100).toFixed(2)}%`;
// };

const TotalAmount = ({ amount, format = true }: TotalAmountProps) => {
  const formatMoney = useFormatMoney();

  return (
    <Box sx={{ textAlign: 'right' }}>
      <Typography fontSize={20} whiteSpace="nowrap">
        {format && typeof amount === 'number' ? formatMoney(moveDecimalPoint(amount, -2)) : amount}
      </Typography>
    </Box>
  );
};

const Amount = ({ amount }: AmountProps) => (
  <Stack spacing={2}>
    <TotalAmount amount={amount} />
  </Stack>
);

interface Props {
  generalStatistics?: GeneralStatisticsResultDto;
  loading?: boolean;
  currency: string;
}

interface Values {
  range: { from: Dayjs | undefined; to: Dayjs | undefined };
}

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    minHeight: '290px',
  },
}));

export const StatisticHeader = ({ generalStatistics, loading, currency }: Props) => {
  const intl = useIntl();
  const formatMoney = useFormatMoney();
  const [pnl, setPnl] = useState<ProfitLossStatisticsResultDto | null>(null);

  const getStatistics = useAsyncCallback(async (values: Values) => {
    // prettier-ignore
    const { data } = await investorApiClient.investors.investorApiControllerGetProfitAndLossStatistics({
      from: values.range.from?.toISOString(),
      to: values.range.to?.toISOString(),
    });

    setPnl(data);
  });

  const rangeOptions = useMemo(() => {
    return [
      {
        value: { from: undefined, to: undefined },
        label: intl.formatMessage({ id: 'since_inception' }),
      },
      {
        value: {
          from: dayjs.utc().subtract(12, 'months').startOf('month'),
          to: dayjs.utc().endOf('month'),
        },
        label: intl.formatMessage({ id: 'last_months' }, { nrOfMonths: 12 }),
      },
      {
        value: {
          from: dayjs.utc().startOf('year'),
          to: dayjs.utc().endOf('year'),
        },
        label: intl.formatMessage({ id: 'this_year' }),
      },
    ];
  }, [intl]);

  const exposureRows = useMemo((): ITableRow[] => {
    const onTime = generalStatistics?.principalOnTime[currency] ?? 0;
    const below31 = generalStatistics?.principalBelow31[currency] ?? 0;
    const above31 = generalStatistics?.principalAbove31[currency] ?? 0;

    return [
      // {
      //   cols: ['', currency],
      // },
      {
        cols: [
          intl.formatMessage({ id: 'investment.status.OnTime' }),
          formatMoney(moveDecimalPoint(onTime, -2)),
        ],
        variant: 'header-accent',
      },
      {
        cols: [
          intl.formatMessage({ id: 'repayment_status.Arrears_<30' }),
          formatMoney(moveDecimalPoint(below31, -2)),
        ],
        divider: true,
        variant: 'header-accent',
      },
      {
        cols: [
          intl.formatMessage({ id: 'repayment_status.Arrears_>31' }),
          formatMoney(moveDecimalPoint(above31, -2)),
        ],
        variant: 'header-accent',
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, generalStatistics, intl.formatMessage]);

  const profitLossRows = useMemo((): ITableRow[] => {
    const profit = pnl?.profits[currency] ?? 0;
    const loss = pnl?.losses[currency] ?? 0;

    return [
      // {
      //   cols: ['', currency],
      // },
      {
        cols: [
          intl.formatMessage({ id: 'profit' }),
          formatMoney(moveDecimalPoint(profit, -2)),
        ],
        variant: 'header-accent',
      },
      {
        cols: [
          intl.formatMessage({ id: 'loss' }),
          formatMoney(moveDecimalPoint(loss, -2)),
        ],
        // divider: true,
        variant: 'header-accent',
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, pnl, intl.formatMessage]);

  const classes = useStyles();

  const expectedReturn = generalStatistics?.upcomingInterest[currency] ?? 0;
  const currentExposure = generalStatistics?.upcomingPrincipal[currency] ?? 0;

  return (
    <Grid item md={12} container spacing={4}>
      <Grid item xs={12} sm={6} md={4}>
        <Card className={classes.card}>
          <Card.Body loadingContent={loading}>
            <Box
              sx={{
                height: 242,
                display: 'grid',
                gridTemplateRows: '1fr auto 1fr',
              }}
            >
              <Box>
                <Typography variant="subtitle2" mb={1}>
                  {intl.formatMessage({ id: 'expected_return' })}{' '}
                  <InlineTooltip
                    size={18}
                    tooltip={intl.formatMessage({
                      id: 'expected_return.dashboard_tooltip',
                    })}
                  />
                </Typography>

                <Amount amount={expectedReturn} />
              </Box>

              <Divider />

              <Box sx={{ pt: 4 }}>
                <Typography variant="subtitle2" mb={1}>
                  {intl.formatMessage({ id: 'current_exposure' })}{' '}
                  <InlineTooltip
                    size={18}
                    tooltip={intl.formatMessage({
                      id: 'current_exposure.dashboard_tooltip',
                    })}
                  />
                </Typography>

                <Amount amount={currentExposure} />
              </Box>
            </Box>
          </Card.Body>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
        <Card sx={{ flex: 1 }}>
          <Card.Header
            variant="condensed"
            title={
              <>
                {intl.formatMessage({ id: 'originator.details.performance' })}{' '}
                <InlineTooltip
                  size={18}
                  tooltip={intl.formatMessage({
                    id: 'performance.dashboard_tooltip',
                  })}
                />
              </>
            }
          />

          <Card.Body loadingContent={loading}>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <TotalAmount amount={generalStatistics?.upcomingPrincipal[currency] ?? 0} />
              </Grid>
              <Grid item xs={12}>
                <DataOverviewTable tableOnly rows={exposureRows} />
              </Grid>
            </Grid>
          </Card.Body>
        </Card>
      </Grid>

      <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
        <Card sx={{ flex: 1 }}>
          <Card.Header
            variant="condensed"
            title={
              <>
                {intl.formatMessage({ id: 'net_profit_n_loss' })}{' '}
                <InlineTooltip
                  size={18}
                  tooltip={intl.formatMessage({
                    id: 'net_profit_n_loss.dashboard_tooltip',
                  })}
                />
              </>
            }
          />
          <Card.Body loadingContent={getStatistics.loading}>
            <Grid container spacing={4} flexWrap="wrap">
              <Grid item xs display="flex" alignItems="center">
                <Formik<Values>
                  initialValues={{
                    range: rangeOptions[0].value,
                  }}
                  onSubmit={(v) => getStatistics.execute(v)}
                >
                  <Form>
                    <Field
                      id="range"
                      name="range"
                      sx={{ width: '150px' }}
                      component={Select}
                      options={rangeOptions}
                      variant="filter"
                      disableSelectedClass
                    />

                    <SubmitFormOnChange initialSubmit />
                  </Form>
                </Formik>
              </Grid>

              <Grid item xs>
                <TotalAmount amount={pnl?.totals[currency] ?? 0} />
              </Grid>
              <Grid item xs={12}>
                <DataOverviewTable tableOnly rows={profitLossRows} />
              </Grid>
            </Grid>
          </Card.Body>
        </Card>
      </Grid>
    </Grid>
  );
};
